<script setup>
import Button from 'dashboard/components-next/button/Button.vue';
import ButtonGroup from 'dashboard/components-next/buttonGroup/ButtonGroup.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { computed, defineProps, toRef } from 'vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { useMapGetter } from 'dashboard/composables/store';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useMatchedSop } from 'dashboard/composables/useMatchedSop';
import { SUPPORTED_CHANNEL_TYPES } from 'dashboard/routes/dashboard/settings/sharedFiles/constants';

const props = defineProps({
  currentChat: {
    required: true,
    type: Object,
  },
});

const { updateUISettings } = useUISettings();

const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const channelType = computed(() => {
  return props.currentChat?.channel || props.currentChat?.meta?.channel || '';
});

const showCopilotTab = computed(() =>
  isFeatureEnabledonAccount.value(currentAccountId.value, FEATURE_FLAGS.CAPTAIN)
);

const showSharedFilesTab = computed(() => {
  const supportedChannel = SUPPORTED_CHANNEL_TYPES.includes(channelType.value);
  const enabledFeature = isFeatureEnabledonAccount.value(
    currentAccountId.value,
    FEATURE_FLAGS.SHARED_FILES
  );
  return enabledFeature && supportedChannel;
});

const showShopeeTab = computed(() => channelType.value === INBOX_TYPES.SHOPEE);
const showLazadaTab = computed(() => channelType.value === INBOX_TYPES.LAZADA);
const showTiktokShopTab = computed(
  () => channelType.value === INBOX_TYPES.TIKTOK_SHOP
);

const { matchedSops } = useMatchedSop(toRef(props, 'currentChat'));
const showSopTab = computed(() => matchedSops.value.length > 0);

const { uiSettings } = useUISettings();
const isContactSidebarOpen = computed(
  () => uiSettings.value.is_contact_sidebar_open
);
const isSharedFilesPanelOpen = computed(
  () => uiSettings.value.is_shared_files_panel_open
);
const isCopilotPanelOpen = computed(
  () => uiSettings.value.is_copilot_panel_open
);
const isShopeePanelOpen = computed(() => uiSettings.value.is_shopee_panel_open);
const isLazadaPanelOpen = computed(() => uiSettings.value.is_lazada_panel_open);
const isTiktokShopPanelOpen = computed(
  () => uiSettings.value.is_tiktok_shop_panel_open
);
const isSopPanelOpen = computed(() => uiSettings.value.is_sop_panel_open);

const toggleConversationSidebarToggle = () => {
  updateUISettings({
    is_contact_sidebar_open: !isContactSidebarOpen.value,
    is_shared_files_panel_open: false,
    is_copilot_panel_open: false,
    is_shopee_panel_open: false,
    is_lazada_panel_open: false,
    is_tiktok_shop_panel_open: false,
    is_sop_panel_open: false,
  });
};

const handleConversationSidebarToggle = () => {
  updateUISettings({
    is_contact_sidebar_open: true,
    is_shared_files_panel_open: false,
    is_copilot_panel_open: false,
    is_shopee_panel_open: false,
    is_lazada_panel_open: false,
    is_tiktok_shop_panel_open: false,
    is_sop_panel_open: false,
  });
};

const handleSharedFilesSidebarToggle = () => {
  updateUISettings({
    is_contact_sidebar_open: false,
    is_shared_files_panel_open: true,
    is_copilot_panel_open: false,
    is_shopee_panel_open: false,
    is_lazada_panel_open: false,
    is_tiktok_shop_panel_open: false,
    is_sop_panel_open: false,
  });
};

const handleCopilotSidebarToggle = () => {
  updateUISettings({
    is_contact_sidebar_open: false,
    is_shared_files_panel_open: false,
    is_copilot_panel_open: true,
    is_shopee_panel_open: false,
    is_lazada_panel_open: false,
    is_tiktok_shop_panel_open: false,
    is_sop_panel_open: false,
  });
};

const handleShopeeSidebarToggle = () => {
  updateUISettings({
    is_contact_sidebar_open: false,
    is_shared_files_panel_open: false,
    is_copilot_panel_open: false,
    is_shopee_panel_open: true,
    is_lazada_panel_open: false,
    is_tiktok_shop_panel_open: false,
    is_sop_panel_open: false,
  });
};

const handleLazadaSidebarToggle = () => {
  updateUISettings({
    is_shared_files_panel_open: false,
    is_contact_sidebar_open: false,
    is_copilot_panel_open: false,
    is_shopee_panel_open: false,
    is_lazada_panel_open: true,
    is_tiktok_shop_panel_open: false,
    is_sop_panel_open: false,
  });
};

const handleTiktokShopSidebarToggle = () => {
  updateUISettings({
    is_contact_sidebar_open: false,
    is_shared_files_panel_open: false,
    is_copilot_panel_open: false,
    is_shopee_panel_open: false,
    is_lazada_panel_open: false,
    is_tiktok_shop_panel_open: true,
    is_sop_panel_open: false,
  });
};

const handleSopSidebarToggle = () => {
  updateUISettings({
    is_contact_sidebar_open: false,
    is_shared_files_panel_open: false,
    is_copilot_panel_open: false,
    is_shopee_panel_open: false,
    is_lazada_panel_open: false,
    is_tiktok_shop_panel_open: false,
    is_sop_panel_open: true,
  });
};

const keyboardEvents = {
  'Alt+KeyO': {
    action: toggleConversationSidebarToggle,
  },
};
useKeyboardEvents(keyboardEvents);
</script>

<template>
  <ButtonGroup
    class="flex flex-col justify-center items-center absolute top-36 xl:top-24 ltr:right-2 rtl:left-2 bg-n-solid-2/90 backdrop-blur-lg border border-n-weak/50 rounded-full gap-1.5 p-1.5 shadow-sm transition-shadow duration-200 hover:shadow z-20"
  >
    <Button
      v-tooltip.top="$t('CONVERSATION.SIDEBAR.CONTACT')"
      ghost
      slate
      sm
      class="!rounded-full transition-all duration-[250ms] ease-out active:!scale-95 active:!brightness-105 active:duration-75"
      :class="{
        'bg-n-alpha-2 active:shadow-sm': isContactSidebarOpen,
      }"
      icon="i-ph-user-bold"
      @click="handleConversationSidebarToggle"
    />
    <Button
      v-if="showSharedFilesTab"
      v-tooltip.bottom="$t('CONVERSATION.SIDEBAR.SHARED_FILES')"
      ghost
      slate
      class="!rounded-full"
      :class="{
        'bg-n-alpha-2 !text-n-iris-9': isSharedFilesPanelOpen,
      }"
      sm
      icon="i-woot-shared-files"
      @click="handleSharedFilesSidebarToggle"
    />
    <Button
      v-if="showCopilotTab"
      v-tooltip.bottom="$t('CONVERSATION.SIDEBAR.COPILOT')"
      ghost
      slate
      sm
      class="!rounded-full transition-all duration-[250ms] ease-out active:!scale-95 active:duration-75"
      :class="{
        'bg-n-alpha-2 !text-n-iris-9 active:!brightness-105 active:shadow-sm':
          isCopilotPanelOpen,
      }"
      icon="i-woot-captain"
      @click="handleCopilotSidebarToggle"
    />
    <Button
      v-if="showShopeeTab"
      v-tooltip.bottom="$t('CONVERSATION.SIDEBAR.SHOPEE')"
      ghost
      slate
      class="!rounded-full"
      :class="{
        'bg-n-alpha-2 !text-n-iris-9': isShopeePanelOpen,
      }"
      sm
      icon="i-woot-shopee"
      @click="handleShopeeSidebarToggle"
    />
    <Button
      v-if="showLazadaTab"
      v-tooltip.bottom="$t('CONVERSATION.SIDEBAR.LAZADA')"
      ghost
      slate
      class="!rounded-full"
      :class="{
        'bg-n-alpha-2 !text-n-iris-9': isLazadaPanelOpen,
      }"
      sm
      icon="i-woot-lazada"
      @click="handleLazadaSidebarToggle"
    />
    <Button
      v-if="showTiktokShopTab"
      v-tooltip.bottom="$t('CONVERSATION.SIDEBAR.TIKTOKSHOP')"
      ghost
      slate
      class="!rounded-full"
      :class="{
        'bg-n-alpha-2 !text-n-iris-9': isTiktokShopPanelOpen,
      }"
      sm
      icon="i-woot-tiktokshop"
      @click="handleTiktokShopSidebarToggle"
    />
    <div v-if="showSopTab" class="relative">
      <Button
        v-tooltip.bottom="$t('CONVERSATION.SIDEBAR.SOP')"
        ghost
        slate
        class="!rounded-full"
        :class="{
          'bg-n-alpha-2': isSopPanelOpen,
        }"
        sm
        icon="i-lucide-workflow"
        @click="handleSopSidebarToggle"
      />
      <!-- Ping until the agent opens the SOP panel -->
      <span
        v-if="!isSopPanelOpen"
        class="absolute top-0 ltr:right-0 rtl:left-0 flex size-2 pointer-events-none"
      >
        <span
          class="animate-ping absolute inline-flex h-full w-full rounded-full bg-n-brand opacity-75"
        />
        <span class="relative inline-flex rounded-full size-2 bg-n-brand" />
      </span>
    </div>
  </ButtonGroup>
</template>
